// ✅ MaterielReparationsPage.js — détail complet réparations matériel

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import { Alert, Badge, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  FaBuilding, FaCalendarAlt, FaFileExcel, FaFilter, FaSearch,
  FaSyncAlt, FaTools, FaTruck, FaChevronDown, FaChevronRight,
  FaTag, FaBoxOpen, FaMoneyBillWave, FaPaperclip
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import DocumentsModal from '../components/DocumentsModal';
import documentService from '../services/documentService';

import '../styles/MaterielReparationsPage.css';

const fmtDate = (d) =>
  d ? String(d).split('T')[0] : '—';

const fmtNum = (v, dec = 2) =>
  v != null && v !== '' ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : '—';

const norm = (s) =>
  String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

// ─── KPI card ───────────────────────────────────────────────
const KpiCard = ({ icon, label, value, sub, color = '#6d4e36' }) => (
  <div className="mr-kpi-card" style={{ borderTop: `3px solid ${color}` }}>
    <div className="mr-kpi-icon" style={{ background: color + '18', color }}>{icon}</div>
    <div>
      <div className="mr-kpi-value" style={{ color }}>{value}</div>
      <div className="mr-kpi-label">{label}</div>
      {sub && <div className="mr-kpi-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Expanded row detail ─────────────────────────────────────
const ExpandedRow = ({ row }) => (
  <div className="mr-expand">
    <div className="mr-expand-grid">

      <div className="mr-expand-section">
        <div className="mr-expand-title"><FaTruck size={12} /> Matériel</div>
        <div className="mr-expand-rows">
          <span className="mr-exp-label">Matricule</span><span className="mr-exp-val">{row.matricule || '—'}</span>
          <span className="mr-exp-label">Type</span><span className="mr-exp-val">{row.materiel_type || '—'}</span>
          <span className="mr-exp-label">Société</span><span className="mr-exp-val">{row.materiel_societe || '—'}</span>
          <span className="mr-exp-label">Chantier</span><span className="mr-exp-val">{row.chantier_name || row.chantier_actuel || '—'}</span>
          <span className="mr-exp-label">État</span><span className="mr-exp-val">{row.materiel_etat || '—'}</span>
        </div>
      </div>

      <div className="mr-expand-section">
        <div className="mr-expand-title"><FaBoxOpen size={12} /> Article BDC</div>
        <div className="mr-expand-rows">
          <span className="mr-exp-label">Désignation</span><span className="mr-exp-val">{row.designation || '—'}</span>
          <span className="mr-exp-label">Code produit</span><span className="mr-exp-val">{row.code_produit || '—'}</span>
          <span className="mr-exp-label">Qté / Unité</span><span className="mr-exp-val">{row.qte != null ? `${fmtNum(row.qte, 2)} ${row.unite || ''}` : '—'}</span>
          <span className="mr-exp-label">Prix HT / u</span><span className="mr-exp-val">{row.prix != null ? `${fmtNum(row.prix)} MAD` : '—'}</span>
          <span className="mr-exp-label">Taxe</span><span className="mr-exp-val">{row.taxe != null ? `${row.taxe}%` : '—'}</span>
          <span className="mr-exp-label">Montant TTC</span><span className="mr-exp-val" style={{ fontWeight: 800, color: '#1565c0' }}>{row.montant_ttc != null ? `${fmtNum(row.montant_ttc)} MAD` : '—'}</span>
        </div>
      </div>

      <div className="mr-expand-section">
        <div className="mr-expand-title"><FaBuilding size={12} /> BDC & Fournisseur</div>
        <div className="mr-expand-rows">
          <span className="mr-exp-label">Réf BDC</span><span className="mr-exp-val">{row.ref_bdc || '—'}</span>
          <span className="mr-exp-label">Date BDC</span><span className="mr-exp-val">{fmtDate(row.date_bdc)}</span>
          <span className="mr-exp-label">Fournisseur</span><span className="mr-exp-val">{row.fournisseur_libelle || '—'}</span>
          <span className="mr-exp-label">Société achat</span><span className="mr-exp-val">{row.societe_name || '—'}</span>
          <span className="mr-exp-label">Date saisie</span><span className="mr-exp-val">{fmtDate(row.line_saisie_date)}</span>
          <span className="mr-exp-label">Enregistré le</span><span className="mr-exp-val">{fmtDate(row.created_at)}</span>
        </div>
      </div>

      {row.commentaire && (
        <div className="mr-expand-section mr-expand-comment">
          <div className="mr-expand-title">💬 Commentaire</div>
          <div className="mr-exp-comment-body">{row.commentaire}</div>
        </div>
      )}

    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────
const MaterielReparationsPage = ({ user }) => {
  const [loading, setLoading]   = useState(true);
  const [rows, setRows]         = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [error, setError]       = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [docCounts, setDocCounts]     = useState({});
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocMateriel, setSelectedDocMateriel] = useState(null);

  // Filtres
  const [globalSearch, setGlobalSearch] = useState('');
  const [fIdMateriel, setFIdMateriel]   = useState('');
  const [fChantier, setFChantier]       = useState('');
  const [fFournisseur, setFournisseur]  = useState('');
  const [fDesignation, setFDesignation] = useState('');
  const [fRefBdc, setFRefBdc]           = useState('');
  const [fStart, setFStart]             = useState('');
  const [fEnd, setFEnd]                 = useState('');

  // Pagination
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  // Sort
  const [sortCol, setSortCol]     = useState('created_at');
  const [sortDir, setSortDir]     = useState('desc');

  const isAdminOrResp = canAccess(user, 'materiels-reparations:write');
  const searchRef = useRef(null);

  // ── Fetch (API) — no filter params, all filtering is client-side ──
  const fetchAll = async () => {
    setLoading(true);
    setError('');
    setExpandedIds(new Set());
    try {
      const [repRes, chRes] = await Promise.all([
        axios.get('/materiel-reparations'),
        axios.get('/chantiers')
      ]);

      const raw =
        Array.isArray(repRes.data) ? repRes.data :
        Array.isArray(repRes.data?.rows) ? repRes.data.rows :
        Array.isArray(repRes.data?.data) ? repRes.data.data : [];

      setRows(raw);
      setChantiers(Array.isArray(chRes.data) ? chRes.data : []);
      setPage(1);

      const idMateriels = [...new Set(raw.map((r) => r.id_materiel).filter(Boolean))];
      if (idMateriels.length) {
        const statsRes = await documentService.getDocumentsStats('materiel', idMateriels);
        setDocCounts(statsRes?.data || {});
      } else {
        setDocCounts({});
      }
    } catch (e) {
      console.error(e);
      setError('❌ Erreur chargement réparations');
    } finally {
      setLoading(false);
    }
  };

  const openDocsModal = (row) => {
    setSelectedDocMateriel({
      id: row.id_materiel,
      matricule: row.matricule,
      type: row.materiel_type
    });
    setShowDocsModal(true);
  };

  const closeDocsModal = () => {
    setShowDocsModal(false);
    setSelectedDocMateriel(null);
    fetchAll();
  };

  const resetFilters = () => {
    setFIdMateriel(''); setFChantier(''); setFournisseur('');
    setFDesignation(''); setFRefBdc(''); setFStart(''); setFEnd('');
    setGlobalSearch(''); setPage(1);
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  // ── Options dérivées des données chargées (seulement valeurs présentes) ──
  const materiels = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => {
      if (r.id_materiel && !seen.has(r.id_materiel))
        seen.set(r.id_materiel, { id: r.id_materiel, matricule: r.matricule || '', type: r.materiel_type || '' });
    });
    return [...seen.values()].sort((a, b) => a.id - b.id);
  }, [rows]);

  const fournisseurOptions = useMemo(() => {
    const s = new Set(rows.map(r => r.fournisseur_libelle).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const designationOptions = useMemo(() => {
    const s = new Set(rows.map(r => r.designation).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const refBdcOptions = useMemo(() => {
    const s = new Set(rows.map(r => r.ref_bdc).filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const chantierOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => {
      const name = r.chantier_name || r.chantier_actuel;
      if (name && !seen.has(name)) seen.set(name, name);
    });
    return [...seen.keys()].sort();
  }, [rows]);

  // ── Filtrage client-side ──
  const filtered = useMemo(() => {
    const q = norm(globalSearch);
    return rows.filter(r => {
      if (fIdMateriel && String(r.id_materiel) !== String(fIdMateriel)) return false;
      if (fChantier && (r.chantier_name || r.chantier_actuel) !== fChantier) return false;
      if (fFournisseur && r.fournisseur_libelle !== fFournisseur) return false;
      if (fDesignation && r.designation !== fDesignation) return false;
      if (fRefBdc && r.ref_bdc !== fRefBdc) return false;
      if (fStart && fmtDate(r.created_at) < fStart) return false;
      if (fEnd   && fmtDate(r.created_at) > fEnd)   return false;
      if (q && !norm([r.matricule, r.materiel_type, r.materiel_societe, r.chantier_name,
            r.chantier_actuel, r.ref_bdc, r.designation, r.code_produit,
            r.fournisseur_libelle, r.societe_name, r.commentaire].join(' ')).includes(q)) return false;
      return true;
    });
  }, [rows, globalSearch, fIdMateriel, fChantier, fFournisseur, fDesignation, fRefBdc, fStart, fEnd]);

  // ── Sort ──
  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortCol] ?? '';
      const bv = b[sortCol] ?? '';
      const cmp = String(av).localeCompare(String(bv), 'fr', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir]);

  // ── Pagination ──
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  // ── KPIs ──
  const kpis = useMemo(() => {
    const totalTTC = filtered.reduce((s, r) => s + (Number(r.montant_ttc) || 0), 0);
    const mats = new Set(filtered.map(r => r.id_materiel).filter(Boolean));
    const bdcs = new Set(filtered.map(r => r.ref_bdc).filter(Boolean));
    return { count: filtered.length, totalTTC, nbMats: mats.size, nbBdcs: bdcs.size };
  }, [filtered]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const exportExcel = () => {
    const data = filtered.map(r => ({
      ID: r.id,
      ID_Materiel: r.id_materiel,
      Matricule: r.matricule || '',
      Type_Materiel: r.materiel_type || '',
      Societe_Materiel: r.materiel_societe || '',
      Chantier: r.chantier_name || r.chantier_actuel || '',
      Ref_BDC: r.ref_bdc || '',
      Date_BDC: fmtDate(r.date_bdc),
      Fournisseur: r.fournisseur_libelle || '',
      Societe_Achat: r.societe_name || '',
      Code_Produit: r.code_produit || '',
      Designation: r.designation || '',
      Qte: r.qte ?? '',
      Unite: r.unite || '',
      Prix_HT: r.prix ?? '',
      Taxe_Pct: r.taxe ?? '',
      Montant_TTC: r.montant_ttc ?? '',
      Commentaire: r.commentaire || '',
      Date_Saisie: fmtDate(r.line_saisie_date),
      Enregistre_Le: fmtDate(r.created_at)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reparations');
    XLSX.writeFile(wb, `Reparations_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const Th = ({ col, label, style = {} }) => {
    const active = sortCol === col;
    return (
      <th onClick={() => handleSort(col)} className={`mr-th ${active ? 'mr-th-active' : ''}`} style={style}>
        {label}
        <span className="mr-sort-arrow">{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </th>
    );
  };

  return (
    <div className="container mt-4 mb-5 mr-page">

      {/* ── Header ── */}
      <div className="mr-header">
        <div className="mr-header-left">
          <FaTools className="mr-header-icon" />
          <div>
            <div className="mr-header-title">Réparations Matériel</div>
            <div className="mr-header-sub">Historique des pièces et articles achats liés aux engins</div>
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button size="sm" variant="outline-secondary" onClick={fetchAll} disabled={loading}>
            <FaSyncAlt className="me-1" /> Actualiser
          </Button>
          {isAdminOrResp && (
            <Button size="sm" variant="outline-success" onClick={exportExcel} disabled={loading || !filtered.length}>
              <FaFileExcel className="me-1" /> Excel
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" className="mt-2">{error}</Alert>}

      {/* ── KPI cards ── */}
      <div className="mr-kpi-row">
        <KpiCard icon={<FaTools size={18} />} label="Lignes réparations" value={kpis.count.toLocaleString('fr-FR')} color="#6d4e36" />
        <KpiCard icon={<FaMoneyBillWave size={18} />} label="Coût total TTC" value={kpis.totalTTC > 0 ? fmtNum(kpis.totalTTC) + ' MAD' : '—'} color="#1565c0" />
        <KpiCard icon={<FaTruck size={18} />} label="Matériels distincts" value={kpis.nbMats} color="#2e7d32" />
        <KpiCard icon={<FaTag size={18} />} label="BDC distincts" value={kpis.nbBdcs} color="#4527a0" />
      </div>

      {/* ── Filtres ── */}
      <div className="mr-filters">
        <div className="mr-filters-header">
          <div className="mr-filters-title"><FaFilter size={11} /> Filtres</div>
          <button className="mr-reset-btn" onClick={resetFilters}>✕ Réinitialiser</button>
        </div>
        <Row className="g-2 align-items-end">

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Label className="mr-filter-label">🚜 Matériel</Form.Label>
            <Form.Select size="sm" value={fIdMateriel} onChange={e => { setFIdMateriel(e.target.value); setPage(1); }}>
              <option value="">— Tous —</option>
              {materiels.map(m => (
                <option key={m.id} value={m.id}>
                  #{m.id}{m.matricule ? ` · ${m.matricule}` : ''}{m.type ? ` (${m.type})` : ''}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Label className="mr-filter-label">🏗️ Chantier</Form.Label>
            <Form.Select size="sm" value={fChantier} onChange={e => { setFChantier(e.target.value); setPage(1); }}>
              <option value="">— Tous —</option>
              {chantierOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Label className="mr-filter-label">🏭 Fournisseur</Form.Label>
            <Form.Select size="sm" value={fFournisseur} onChange={e => { setFournisseur(e.target.value); setPage(1); }}>
              <option value="">— Tous —</option>
              {fournisseurOptions.map(f => <option key={f} value={f}>{f}</option>)}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Label className="mr-filter-label">📦 Désignation</Form.Label>
            <Form.Select size="sm" value={fDesignation} onChange={e => { setFDesignation(e.target.value); setPage(1); }}>
              <option value="">— Toutes —</option>
              {designationOptions.map(d => <option key={d} value={d}>{d}</option>)}
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} md={4} lg={2}>
            <Form.Label className="mr-filter-label">📋 Réf BDC</Form.Label>
            <Form.Select size="sm" value={fRefBdc} onChange={e => { setFRefBdc(e.target.value); setPage(1); }}>
              <option value="">— Toutes —</option>
              {refBdcOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </Form.Select>
          </Col>

          <Col xs={6} sm={3} md={2} lg={1}>
            <Form.Label className="mr-filter-label">📅 Début</Form.Label>
            <Form.Control size="sm" type="date" value={fStart} onChange={e => { setFStart(e.target.value); setPage(1); }} />
          </Col>

          <Col xs={6} sm={3} md={2} lg={1}>
            <Form.Label className="mr-filter-label">📅 Fin</Form.Label>
            <Form.Control size="sm" type="date" value={fEnd} onChange={e => { setFEnd(e.target.value); setPage(1); }} />
          </Col>

        </Row>
      </div>

      {/* ── Search + pagination controls ── */}
      <div className="mr-toolbar">
        <div className="mr-search-wrap">
          <FaSearch className="mr-search-icon" />
          <input
            ref={searchRef}
            className="mr-search-input"
            placeholder="Recherche rapide : matricule, fournisseur, désignation, ref…"
            value={globalSearch}
            onChange={e => { setGlobalSearch(e.target.value); setPage(1); }}
          />
          {globalSearch && <button className="mr-search-clear" onClick={() => setGlobalSearch('')}>✕</button>}
        </div>
        <div className="mr-pagination-controls">
          <span className="mr-count">
            {sorted.length > 0
              ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, sorted.length)} sur ${sorted.length}`
              : '0 résultat'}
          </span>
          <Form.Select size="sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ width: 80 }}>
            {[15, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </Form.Select>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="d-flex align-items-center gap-2 mt-4">
          <Spinner animation="border" size="sm" />
          <span>Chargement…</span>
        </div>
      ) : sorted.length === 0 ? (
        <div className="mr-empty">Aucune réparation trouvée</div>
      ) : (
        <div className="mr-table-wrap">
          <table className="mr-table">
            <thead>
              <tr>
                <th className="mr-th mr-th-exp" />
                <Th col="matricule"          label="Matériel" />
                <Th col="chantier_name"      label="Chantier" />
                <Th col="ref_bdc"            label="Réf BDC" />
                <Th col="date_bdc"           label="Date BDC" />
                <Th col="fournisseur_libelle" label="Fournisseur" />
                <Th col="designation"        label="Désignation" style={{ minWidth: 180 }} />
                <Th col="qte"                label="Qté" style={{ textAlign: 'right' }} />
                <Th col="prix"               label="PU HT" style={{ textAlign: 'right' }} />
                <Th col="montant_ttc"        label="TTC" style={{ textAlign: 'right' }} />
                <Th col="created_at"         label="Enregistré" />
              </tr>
            </thead>
            <tbody>
              {paginated.map(row => {
                const expanded = expandedIds.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`mr-row ${expanded ? 'mr-row-expanded' : ''}`}
                      onClick={() => toggleExpand(row.id)}
                    >
                      <td className="mr-td-exp">
                        {expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                      </td>

                      {/* Matériel */}
                      <td>
                        <div className="mr-mat-cell">
                          <span className="mr-mat-id">#{row.id_materiel}</span>
                          {row.matricule && <span className="mr-mat-matricule">{row.matricule}</span>}
                          {row.materiel_type && <Badge bg="secondary" className="mr-mat-type">{row.materiel_type}</Badge>}
                          {Number(docCounts[row.id_materiel] || 0) > 0 && (
                            <Badge
                              bg="info"
                              text="dark"
                              title="Documents disponibles pour ce matériel"
                              onClick={(e) => { e.stopPropagation(); openDocsModal(row); }}
                              style={{ cursor: 'pointer' }}
                              className="mr-doc-badge"
                            >
                              <FaPaperclip className="me-1" />
                              {docCounts[row.id_materiel]}
                            </Badge>
                          )}
                        </div>
                        {row.materiel_societe && <div className="mr-mat-soc">{row.materiel_societe}</div>}
                      </td>

                      {/* Chantier */}
                      <td className="mr-td-muted">{row.chantier_name || row.chantier_actuel || '—'}</td>

                      {/* Réf BDC */}
                      <td><span className="mr-ref-bdc">{row.ref_bdc || '—'}</span></td>

                      {/* Date BDC */}
                      <td className="mr-td-muted">{fmtDate(row.date_bdc)}</td>

                      {/* Fournisseur */}
                      <td className="mr-td-fournisseur">{row.fournisseur_libelle || '—'}</td>

                      {/* Désignation */}
                      <td>
                        <div className="mr-designation">{row.designation || '—'}</div>
                        {row.code_produit && <div className="mr-code">{row.code_produit}</div>}
                      </td>

                      {/* Qté */}
                      <td className="mr-td-num">
                        {row.qte != null ? <><strong>{fmtNum(row.qte, 2)}</strong> <span className="mr-unite">{row.unite || ''}</span></> : '—'}
                      </td>

                      {/* PU HT */}
                      <td className="mr-td-num mr-td-muted">
                        {row.prix != null ? `${fmtNum(row.prix)} MAD` : '—'}
                      </td>

                      {/* TTC */}
                      <td className="mr-td-num">
                        {row.montant_ttc != null
                          ? <span className="mr-ttc">{fmtNum(row.montant_ttc)} MAD</span>
                          : '—'}
                      </td>

                      {/* Date enregistrement */}
                      <td className="mr-td-muted">{fmtDate(row.created_at)}</td>
                    </tr>

                    {expanded && (
                      <tr className="mr-row-detail">
                        <td colSpan={11} className="mr-td-detail">
                          <ExpandedRow row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>

            {/* Total footer */}
            {filtered.length > 0 && (
              <tfoot>
                <tr className="mr-foot">
                  <td colSpan={9} className="mr-foot-label">Total ({filtered.length} lignes filtrées)</td>
                  <td className="mr-td-num mr-foot-ttc">{fmtNum(kpis.totalTTC)} MAD</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="mr-pager">
          <button className="mr-pg-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
          <button className="mr-pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push(null);
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === null
                ? <span key={`e${i}`} className="mr-pg-ellipsis">…</span>
                : <button key={p} className={`mr-pg-btn ${p === page ? 'mr-pg-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
            )}
          <button className="mr-pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button className="mr-pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}

      <DocumentsModal
        show={showDocsModal}
        onHide={closeDocsModal}
        module="materiel"
        moduleId={selectedDocMateriel?.id}
        itemLabel={
          selectedDocMateriel
            ? `${selectedDocMateriel.matricule || ''}${selectedDocMateriel.type ? ` - ${selectedDocMateriel.type}` : ''}`
            : ''
        }
        title="Documents du matériel"
        canDelete={isAdminOrResp}
      />

    </div>
  );
};

export default MaterielReparationsPage;
